// ═══════════════════════════════════════════════════════════════════════════════
//  AssessmentRunner — conduce el examen
//
//  Su única responsabilidad es el ciclo: pintar el reactivo, capturar la
//  respuesta, medir el tiempo y pedir el siguiente. La decisión de QUÉ viene
//  después es del servidor — este componente no sabe de niveles ni de escaleras.
//
//  ⚠️  SIN FEEDBACK DE ACIERTO. Ver la nota en ItemRenderer.jsx: revelar la
//      respuesta a mitad de un examen de nivel contamina la medición.
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useRef, useState } from 'react';
import ItemRenderer from './ItemRenderer';
import { submitAnswer } from './assessmentApi';
import { cancelSpeech } from '../../utils/browserSpeech';

const SKILL_ICON = {
  use_of_english: '🧩',
  reading: '📖',
  listening: '🎧',
  speaking: '🗣️',
  writing: '✍️',
};

export default function AssessmentRunner({ sessionId, firstItem, answeredSoFar, onFinished }) {
  const [item, setItem] = useState(firstItem);
  const [answered, setAnswered] = useState(answeredSoFar || 0);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [pending, setPending] = useState(null);   // respuesta a reintentar

  const startedAt = useRef(Date.now());

  useEffect(() => {
    startedAt.current = Date.now();
    setError(null);
  }, [item?.item_id]);

  useEffect(() => () => cancelSpeech(), []);

  const send = useCallback(
    async (response) => {
      if (sending || !item) return;
      setSending(true);
      setError(null);
      cancelSpeech();

      try {
        const data = await submitAnswer({
          sessionId,
          itemId: item.item_id,
          response,
          timeMs: Date.now() - startedAt.current,
        });

        setPending(null);

        if (data.finished) {
          onFinished(data);
          return;
        }

        setAnswered(data.answered ?? answered + 1);
        setItem(data.item);
      } catch (e) {
        // La respuesta NO se pierde: se guarda para reintentar. En un examen
        // de 15 minutos, perder una respuesta por un bache de red es
        // inaceptable — y el endpoint es idempotente, así que reenviar es seguro.
        setPending(response);
        setError(e.message || 'Se cayó la conexión.');
      } finally {
        setSending(false);
      }
    },
    [sending, item, sessionId, answered, onFinished]
  );

  if (!item) return null;

  return (
    <div className="as-screen">
      <div className="as-runbar">
        <span className="as-badge">
          <span aria-hidden="true">{SKILL_ICON[item.skill] || '•'}</span>
          {item.skill_label}
        </span>
        <span className="as-count">{answered + 1}</span>
      </div>

      {/* El nivel del reactivo NO se muestra: saberlo condiciona la respuesta
          y genera ansiedad innecesaria en los escalones altos. */}

      <div className="as-item" key={item.item_id}>
        <ItemRenderer item={item} onSubmit={send} disabled={sending} />
      </div>

      {sending && <p className="as-hint as-center">Guardando…</p>}

      {error && (
        <div className="as-error">
          <p>{error}</p>
          <button
            type="button"
            className="as-btn"
            onClick={() => send(pending)}
            disabled={sending}
          >
            Reintentar
          </button>
          <p className="as-hint">
            Tu respuesta no se perdió. Si prefieres, cierra y vuelve más tarde:
            el examen retoma justo aquí.
          </p>
        </div>
      )}
    </div>
  );
}
